/**
 * Build canonical project preview/canvas URLs — canvas edition.
 *
 * Mirrors the logic in `smbls/packages/cli/bin/start.js:27-46` and
 * `smbls/packages/cli/helpers/projectKeyUtils.js` (§46.4 / §44 compat).
 *
 * The CLI is the authoritative reference. Keep this in sync with it.
 * Do NOT import the CLI package from canvas — it runs in the browser.
 *
 * normalizeProjectKey strips all legacy compound / slash / .symbo.ls-suffix
 * forms that pre-§45 installs may still carry. Required so we never compose
 * 4-level subdomains (the §44 "greenig.symbo.ls" malformation incident).
 */

/**
 * Normalize a raw project key to a bare slug, stripping:
 *   - `.symbo.ls` hostname suffix
 *   - slash-form (take last path segment)
 *   - owner-prefixed combined form (`owner--key` → `key`)
 *
 * Pass `owner` for owner-aware stripping; without it we fall back to
 * stripping everything up to the first `--` (safe for single-token owners).
 *
 * @param {string} raw   - raw key from state (may be any legacy form)
 * @param {string} [owner] - owner slug for owner-aware combined-form stripping
 * @returns {string} bare slug, or '' if empty
 */
export function normalizeProjectKey (raw, owner) {
  let s = String(raw || '').trim()
  if (!s) return ''

  // Strip legacy .symbo.ls suffix first so suffix+prefix forms compose.
  if (/\.symbo\.ls$/i.test(s)) s = s.replace(/\.symbo\.ls$/i, '')

  // Slash form → take the part after the last slash.
  if (s.includes('/')) s = s.split('/').pop()

  // Owner-aware combined-form strip.
  const ownerStr = typeof owner === 'string' ? owner.trim() : ''
  if (ownerStr && s.startsWith(`${ownerStr}--`)) {
    s = s.slice(ownerStr.length + 2)
  } else if (!ownerStr && s.includes('--')) {
    // Best-effort without owner context: strip up to and including first `--`.
    // Safe when the owner is a single token. Callers SHOULD pass owner.
    s = s.slice(s.indexOf('--') + 2)
  }

  return s
}

/**
 * Build the full set of canonical project URLs for a given env.
 *
 * Returns `null` when either owner or key normalizes to empty (so callers
 * can disable / omit links instead of generating malformed URLs).
 *
 * @param {string} owner   - raw project owner
 * @param {string} key     - raw project key (any form)
 * @param {string} [env]   - deployment env; defaults to 'development'
 * @returns {{ safeOwner, safeKey, preview, canvas, directDev, directProd } | null}
 */
export function buildProjectUrls (owner, key, env = 'development') {
  const safeOwner = String(owner || '').trim().toLowerCase()
  const safeKey = normalizeProjectKey(key, safeOwner)

  if (!safeOwner || !safeKey) return null

  return {
    safeOwner,
    safeKey,
    // preview.symbols.app always uses path form + ?env param
    preview: `https://preview.symbols.app/${safeOwner}/${safeKey}?env=${env}`,
    // edit.symbols.app (cloud canvas)
    canvas: `https://edit.symbols.app/${safeOwner}/${safeKey}?env=${env}`,
    // direct subdomain: production has no env infix; others do
    directDev: `https://${safeKey}--development.${safeOwner}.symbo.ls`,
    directProd: `https://${safeKey}.${safeOwner}.symbo.ls`
  }
}
